import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Ban, ShieldAlert, Clock, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

type Props = {
  userId: string;
  banReason: string | null;
  bannedUntil: string | null;
  onSignOut: () => Promise<void> | void;
};

type Dispute = {
  id: string;
  message: string;
  status: "pending" | "approved" | "denied";
  staff_response: string | null;
  created_at: string;
  resolved_at: string | null;
};

const BannedScreen = ({ userId, banReason, bannedUntil, onSignOut }: Props) => {
  const { toast } = useToast();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase
      .from("ban_disputes" as any)
      .select("id, message, status, staff_response, created_at, resolved_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false }) as any);
    setDisputes((data ?? []) as Dispute[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel(`dispute-${userId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "ban_disputes", filter: `user_id=eq.${userId}` },
        () => load())
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "user_risk_scores", filter: `user_id=eq.${userId}` },
        (payload) => {
          const r = payload.new as any;
          if (!r.is_banned) {
            toast({ title: "You're back!", description: "Your appeal was approved. Reloading…" });
            setTimeout(() => window.location.reload(), 1500);
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const submit = async () => {
    if (message.trim().length < 10) {
      toast({ title: "Too short", description: "Write at least 10 characters.", variant: "destructive" });
      return;
    }
    setSubmitting(true);

    // Ensure session is present (auth.uid() returns null on the server otherwise)
    const { data: sessionData, error: sessErr } = await supabase.auth.getSession();
    console.log("[BannedScreen] session check before dispute:", {
      hasSession: !!sessionData?.session,
      userId: sessionData?.session?.user?.id,
      sessErr,
    });

    if (!sessionData?.session) {
      // Try to refresh once before giving up
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (!refreshed?.session) {
        setSubmitting(false);
        toast({
          title: "Session expired",
          description: "Please sign back in to submit your appeal.",
          variant: "destructive",
        });
        return;
      }
    }

    const { error } = await supabase.rpc("submit_ban_dispute" as any, { p_message: message.trim() });
    setSubmitting(false);
    console.log("[BannedScreen] submit_ban_dispute result:", { error });
    if (error) {
      toast({ title: "Couldn't submit", description: error.message, variant: "destructive" });
      return;
    }
    setMessage("");
    toast({ title: "Appeal submitted", description: "Our team will review it shortly." });
    load();
  };

  const pending = disputes.find((d) => d.status === "pending");
  const isPermanent = !bannedUntil;

  // Live countdown — ticks every second so the user sees the timer drop in real time.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remainingMs = useMemo(() => {
    if (!bannedUntil) return null;
    return Math.max(0, new Date(bannedUntil).getTime() - now);
  }, [bannedUntil, now]);

  const expired = remainingMs !== null && remainingMs <= 0;

  // Auto-reload as soon as the ban expires — restores access without user action.
  useEffect(() => {
    if (!expired) return;
    const t = setTimeout(() => window.location.reload(), 600);
    return () => clearTimeout(t);
  }, [expired]);

  const formatRemaining = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <Card className="p-6 border-destructive/40 bg-destructive/5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 rounded-full bg-destructive/15">
              <Ban className="w-6 h-6 text-destructive" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Account suspended</h1>
              <p className="text-xs text-muted-foreground">Access to TalkLot is currently restricted</p>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-card border border-border">
              <ShieldAlert className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground">Reason</div>
                <p className="break-words">{banReason || "Violation of community guidelines"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-card border border-border">
              <Clock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="min-w-0 w-full">
                <div className="text-[10px] uppercase font-semibold text-muted-foreground">Duration</div>
                {isPermanent ? (
                  <p>Permanent suspension</p>
                ) : expired ? (
                  <p className="text-emerald-500 font-semibold">Expired — restoring access…</p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Until {new Date(bannedUntil!).toLocaleString()}
                    </p>
                    <p className="mt-1 font-mono text-2xl font-bold text-destructive tabular-nums">
                      {formatRemaining(remainingMs!)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">time remaining</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Dispute / Appeal */}
        <Card className="p-5">
          <h2 className="font-semibold text-base mb-1">Appeal this decision</h2>
          <p className="text-xs text-muted-foreground mb-3">
            If you believe this was a mistake, send our moderators a short explanation. You'll be notified here when it's reviewed.
          </p>

          {pending ? (
            <div className="p-3 rounded-lg bg-muted border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                <span className="text-xs font-semibold">Appeal under review</span>
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {formatDistanceToNow(new Date(pending.created_at), { addSuffix: true })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground italic">"{pending.message}"</p>
            </div>
          ) : (
            <>
              <Textarea
                placeholder="Explain why your account should be reinstated…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[110px] text-sm"
                maxLength={1000}
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-[10px] text-muted-foreground">{message.length}/1000</span>
                <Button onClick={submit} disabled={submitting || message.trim().length < 10} size="sm">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit appeal"}
                </Button>
              </div>
            </>
          )}
        </Card>

        {/* History */}
        {!loading && disputes.length > 0 && (
          <Card className="p-4">
            <h3 className="font-semibold text-sm mb-2">Appeal history</h3>
            <div className="space-y-2">
              {disputes.map((d) => (
                <div key={d.id} className="p-2.5 rounded-lg border border-border bg-card text-xs space-y-1">
                  <div className="flex items-center gap-2">
                    {d.status === "approved" && <Badge className="bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>}
                    {d.status === "denied" && <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Denied</Badge>}
                    {d.status === "pending" && <Badge variant="outline">Pending</Badge>}
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {formatDistanceToNow(new Date(d.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-muted-foreground italic break-words">"{d.message}"</p>
                  {d.staff_response && (
                    <div className="p-2 rounded bg-muted mt-1">
                      <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-0.5">Moderator reply</div>
                      <p className="break-words">{d.staff_response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        <Button variant="outline" className="w-full" onClick={() => onSignOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
};

export default BannedScreen;
